<script type="ts">
  import { t, locale } from "../translations/i18n";
  import FaRegEdit from 'svelte-icons/fa/FaRegEdit.svelte';
  import FaRegTrashAlt from 'svelte-icons/fa/FaRegTrashAlt.svelte';
  import IoMdCheckmark from 'svelte-icons/io/IoMdCheckmark.svelte';
  import IoMdClose from 'svelte-icons/io/IoMdClose.svelte';
  import { createEventDispatcher, onMount } from "svelte";
  import { getViewData } from "../data-views/utils";
  import type { View, ViewEntries } from "../data-views/types";
  import { VisitorySource } from "../data-sources/visitory-source";
  import { trackViewLoaded } from "../utils/analytics";


  export let state: { [key: string]: string | string[] } = {};
  export let key: string = "";
  export let disabled: boolean = false;

  const dispatch = createEventDispatcher();
  
  const visitorySource = new VisitorySource();
  let newInputName: string = ""; // New view name input
  let dropdownOpen: boolean = false; // Dropdown open state
  let message: string = ""; // Message to display for the user
  let viewEntries: ViewEntries = {
    user_views:[] as View[],
    organization_views: [] as View[],
  };
  let userViewCollection: View[] = []; // User views
  let organizationViewCollection: View[] = []; // Organization views
  let allViews: View[] = [];
  let editedViewDict: any = {}; 
  let modifiedNames: any = {}; 
  let deleteViewDict: { [key: string]: boolean } = {};

  // Dictionary of messages for different scenarios
  let messagesDict: { [key: string]: string } = {
    "success": $t("saved"),
    "update": $t("updated"),
    "error": $t("Error saving view. Please try again."),
    "emptyError": $t("Please enter a name."),
    "overwrite": $t("View already exists. Do you want to overwrite it?"),
    "duplicated": $t("View already exists. Please choose another name."),
    "org_duplicated": $t("View already exists on your organization. Please choose another name."),
  };
  let savedFlag = false;
  let userName: any = "";
  let inputMatchOrgView: boolean = false;
  let currentIndex = -1;

  // Reactive statement for userViewCollection
  $: userViewCollection = viewEntries.user_views.slice().sort((a: View, b: View) => {
    return new Date(b.latest_update).getTime() - new Date(a.latest_update).getTime();
  });

  // Reactive statement for organizationViewCollection
  $: organizationViewCollection = viewEntries.organization_views.slice().sort((a: View, b: View) => {
    return new Date(b.latest_update).getTime() - new Date(a.latest_update).getTime();
  });

  // Reactive statement to update allViews whenever viewEntries changes
  $: allViews = [
    ...viewEntries.user_views.sort((a: View, b: View) => {
      return new Date(b.latest_update).getTime() - new Date(a.latest_update).getTime();
    }),
    ...viewEntries.organization_views.sort((a: View, b: View) => {
      return new Date(b.latest_update).getTime() - new Date(a.latest_update).getTime();
    })
  ];



  $: userName = viewEntries.user_views.length > 0 ? viewEntries.user_views[0].user : "";

  // Reactive statement to update the button text based on the input name
  $: buttonText = checkDuplicate(newInputName) ? "Overwrite" : "Save";

  // Reactive statement to disable the save button based on the input name
  $: saveButtonDisabled = newInputName === "" || inputMatchOrgView;

  const getViewList = async () => {
    viewEntries = await visitorySource.getViewEntries();
  };

  // Reset dropdown state when clicking outside
  const closeDropdown = (): void => {
    dropdownOpen = false;
    message = "";
    newInputName = "";
    savedFlag = false;
    editedViewDict = {};
    modifiedNames = {};
    deleteViewDict = {};
  };

  const toggleDropdownHandler = (event: Event): void => {
    event.stopPropagation();
    if (disabled) return;

    const opening = !dropdownOpen;
    document.body.click(); // click "outside" to close dropdowns
    if (opening) {
      currentIndex = -1;
      dropdownOpen = true;
    }
  };

  /**
   * Choose an option from the dropdown and load the view
   * @param event
   * @param currentViewName - The name of the view to load
   * @returns void
  */
  const chooseOptionHandler = (event: Event, currentViewName: string): void => {
    event.stopPropagation();
    allViews.find((item: View) => {
      if (item.name === currentViewName) {
        // Track view load
        const viewType = viewEntries.user_views.some(v => v.name === item.name) ? 'user_view' : 'organization_view';
        trackViewLoaded(item.name, viewType);
        dispatch("loadView", item);
      }
    });
    dropdownOpen = false;
  };

  /**
   * Save the current view
   * @param event
   * @param name - The name of the view to save
   * @returns void
  */
  const saveHandler = async (event: Event, name: string): Promise<void> => {
    newInputName = "";
    savedFlag = false;
    event.stopPropagation();

    const view = {
        name: name.toLowerCase(),
        latest_update: new Date().toISOString().split('T')[0],
        view: getViewData(),
        userName: userName,
    };

    const isUpdate = checkDuplicate(name);
    const response = await visitorySource.saveView(view, isUpdate);

    if (response.status === 200) {

        message = `${name} ${isUpdate ? messagesDict["update"] : messagesDict["success"]}.`;
        savedFlag = true;

        // Username is usually taken from existing views
        // Make an API call to get the username for the first view
        if (viewEntries.user_views.length === 0) {
          getViewList();
          return;
        }
        viewEntries = isUpdate
          ? {
              user_views: viewEntries.user_views.map((item: View) => item.name === name ? view : item),
              organization_views: viewEntries.organization_views.map((item: View) => item.name === name ? view : item),
            }
          : {
              user_views: [...viewEntries.user_views, view],
              organization_views: viewEntries.organization_views,
            };

    } else {
        message = response.status.toString().concat(messagesDict["error"]);
    }
  };

  /**
   * Trigger delete confirmation
   * @param event
   * @param currentViewName - The name of the view to delete
   * @returns void
  */
  const triggerDeleteConfirmation = (event: Event, currentViewName: string): void => {
    event.stopPropagation();
    removeFocus();
    deleteViewDict[currentViewName] = true;
  };

  /**
   * Delete a view
   * @param event
   * @param currentViewName - The name of the view to delete
   * @returns void
  */
  const deleteHandler = (event: Event, currentViewName: string): void => {
    event.stopPropagation();
    // Delete view from the database
    visitorySource.deleteView(currentViewName);
    // Remove view from the view list
    viewEntries = {
      user_views: viewEntries.user_views.filter((item: View) => item.name !== currentViewName),
      organization_views: viewEntries.organization_views.filter((item: View) => item.name !== currentViewName),
    };
  };

  /**
   * Edit the name of a view
   * @param event
   * @param currentViewName - The name of the view to edit
   * @returns void
  */
  const editViewHandler = (event: Event, currentViewName: string): void => {
    editedViewDict[currentViewName] = true;
    event.stopPropagation();
    removeFocus();
  };

  /**
   * Update the name of a view
   * @param event
   * @param currentViewName - The name of the view to update
   * @returns void
  */
  const updateViewNameHandler = (event: Event, currentViewName: string): void => {
    event.stopPropagation();
    if (modifiedNames[currentViewName] === "" || modifiedNames[currentViewName] === undefined) {
      abortEditHandler(event, currentViewName);
      return;
    } else if (checkDuplicate(modifiedNames[currentViewName])) {
      message = messagesDict["duplicated"];
      return;
    }

    // Send an update to the database
    visitorySource.saveView({
      name: currentViewName,
      latest_update: new Date().toISOString().split('T')[0],
      view: getViewData(),
    }, true, modifiedNames[currentViewName]);

    // Update view entries
    viewEntries = {
      user_views: viewEntries.user_views.map((item: View) => item.name === currentViewName ? {
        name: modifiedNames[currentViewName],
        latest_update: new Date().toISOString().split('T')[0],
        view: getViewData(),
        user: userName,
      } : item),
      organization_views: viewEntries.organization_views.map((item: View) => item.name === currentViewName ? {
        name: modifiedNames[currentViewName],
        latest_update: new Date().toISOString().split('T')[0],
        view: getViewData(),
      } : item),
    };

    // At the end, clean oldViewName and editedViewDict
    delete editedViewDict[currentViewName];
    delete modifiedNames[currentViewName];
  };

  /**
   * Abort the edit of a view
   * @param event
   * @param currentViewName - The name of the view to abort the edit
   * @returns void
  */
  const abortEditHandler = (event: Event, currentViewName: string): void => {
    event.stopPropagation();
    delete editedViewDict[currentViewName];
    delete modifiedNames[currentViewName];
    delete deleteViewDict[currentViewName];
    // Trigger reactive update
    viewEntries = {
      user_views: viewEntries.user_views,
      organization_views: viewEntries.organization_views,
    };
  };
  
  /**
   * Check if the new input name is a duplicate
   * @param newInputName - The new input name to check
   * @returns boolean
  */
  const checkDuplicate = (newInputName: string): boolean => {
    let duplicate = false;
    savedFlag = false;
    inputMatchOrgView = false;
    message = "";
    viewEntries.user_views.forEach((item: View) => {
      if (item.name === newInputName.toLowerCase()) {
        duplicate = true;
        message = messagesDict["overwrite"];
      }
    });
    viewEntries.organization_views.forEach((item: View) => {
      if (item.name === newInputName.toLowerCase()) {
        duplicate = false;
        inputMatchOrgView = true;
        message = messagesDict["org_duplicated"];
      }
    });
    return duplicate;
  };

  const capitalizeFirstLetter = (view_name: string): string =>{
    return view_name.charAt(0).toUpperCase() + view_name.slice(1);
  }

  /**
   * Handle keyboard events
   * @param event - The keyboard event
   * @returns void
  */
  const onKeyDown = (event: KeyboardEvent): void => {
    if (!dropdownOpen) return;
    switch (event.key) {
      case "Escape":
        closeDropdown();
        break;

      case "Enter":
        if (currentIndex !== -1) {
          const element = document.getElementById(`view-item-${currentIndex}`);
          if (element) {
            element.click();
          }
        }
        break;

      case "ArrowDown":
        currentIndex = (currentIndex + 1) % allViews.length;
        focusCurrentView();
        break;

      case "ArrowUp":
        currentIndex = (currentIndex - 1 + allViews.length) % allViews.length;
        focusCurrentView();
        break;

      case "Tab":
        currentIndex = (currentIndex + 1) % (allViews.length + 1);
        if (currentIndex === allViews.length) {
          focusEnterViewName();
          currentIndex = -1;
        } else {
          focusCurrentView();
        }
        break;

      default:
        // Allow default behavior for other keys
        return;
    }

    // Prevent default behavior only for relevant keys
    event.preventDefault();
  };

  /**
   * Focus the current view
   * @returns void
  */
  const focusCurrentView = () => {
    const viewDropdown = document.getElementById(`views-dropdown`);
    const viewElement = viewDropdown?.querySelectorAll('li');
    if (viewElement && viewElement[currentIndex]) {
      viewElement[currentIndex].focus();
      viewElement[currentIndex].scrollIntoView({ block: "nearest" });
    }
  };

  /**
   * Focus the enter view name input
   * press enter to save the view
   * @returns void
  */
  const focusEnterViewName = () => {
    const enterViewName = document.getElementById("enterViewName");
    if (enterViewName) {
      (enterViewName as HTMLElement).focus();
      enterViewName.addEventListener("keydown", (event) => {
        if ((event as KeyboardEvent).key === "Enter") {
          const saveButton = document.getElementById("saveButton");
          if (saveButton) {
            (saveButton as HTMLElement).click();
          }
        }
      });
    }
  };

  /**
   * Remove focus from all elements
   * @returns void
  */
  const removeFocus = () => {
    currentIndex = -1;
    const elementId = `view-item-${currentIndex}`;
    const viewElement = document.getElementById(elementId);
    if (viewElement) {
      (viewElement as HTMLElement).blur();
    }
  };

  onMount(async () => {
    getViewList();
  });

</script>

<svelte:window on:click={closeDropdown}/>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div class="dropdown" class:disabled on:click={toggleDropdownHandler} id="views-dropdown" on:keydown={onKeyDown} tabindex="0"
  >
  <slot name="icon" />
  {#if dropdownOpen}
    <div class="dropdown-content">
      <ul>
        <!-- User's own views -->
        <p class="dropdown-title">{$t("My Views")}</p>
        <!-- If there are no views, display a message -->
        {#if userViewCollection.length === 0}
          <li class="li-empty-list-indicator">
              {$t("No views")}
          </li>
        {:else}
          {#each Object.values(userViewCollection) as currentView, index}
            <li
              value={currentView.name}
              class:selected={currentView.name === state[key]}
              class:focused={currentIndex === index}
              class:edit-mode={Object.keys(editedViewDict).includes(currentView.name)}
              class:delete-mode={Object.keys(deleteViewDict).includes(currentView.name)}
              on:click={(event) => chooseOptionHandler(event, currentView.name)}
              on:focus={() => currentIndex = index}
              tabindex="0"
              id=view-item-${index}
            >
            <div class="li-upper-content">
              {#if Object.keys(deleteViewDict).includes(currentView.name)}
              <!-- Deleting single view -->
                <div class="li-left-content">
                  <p style="margin: 0;">Delete "{currentView.name}"?</p>
                </div>
                <div class="li-right-content">
                  <div class="confirm" on:click={(event) => deleteHandler(event, currentView.name)}><IoMdCheckmark/></div>
                  <div class="cancel" on:click={(event) => abortEditHandler(event, currentView.name)}><IoMdClose/></div>
                </div>
              {:else if Object.keys(editedViewDict).includes(currentView.name)}
              <!-- Edit single view -->
                <div class="li-left-content">
                  <input
                    class="edit-view-name"
                    type="text"
                    bind:value={modifiedNames[currentView.name]}
                    on:click={(event) => {event.stopPropagation()}}
                    placeholder={currentView.name}
                  />
                </div>
                <div class="li-right-content">
                  <div class="confirm" on:click={(event) => updateViewNameHandler(event, currentView.name)}><IoMdCheckmark/></div>
                  <div class="cancel" on:click={(event) => abortEditHandler(event, currentView.name)}><IoMdClose/></div>
                </div>
              {:else}
                <!-- Default state of a view -->
                <div class="li-left-content">
                  <div class="view-name">{capitalizeFirstLetter(currentView.name)}</div>
                </div>
                <div class="li-right-content">
                  <div class="edit-button" on:click={(event) => editViewHandler(event, currentView.name)}><FaRegEdit /></div>
                  <div class="delete-button" on:click={(event) => {triggerDeleteConfirmation(event, currentView.name)}}><FaRegTrashAlt /></div>
                </div>
              {/if}
            </div>
            <div class="li-lower-content">
              <p class="subtitle-view-name">{userName}, {currentView.latest_update}</p>
            </div>
            </li>
          {/each}
        {/if}
        <!-- Organization Views -->
        <p class="dropdown-title organization">{$t("Organization Views")}</p>
        <!-- If there are no views, display a message -->
        {#if organizationViewCollection.length === 0}
          <li class="li-empty-list-indicator">
            {$t("No organization views")}
          </li>
        {:else}
          {#each Object.values(organizationViewCollection) as currentView, index}
            <li
              value={currentView.name}
              class:selected={currentView.name === state[key]}
              class:focused={currentIndex === index + userViewCollection.length}
              class:edit-mode={Object.keys(editedViewDict).includes(currentView.name)}
              class:delete-mode={Object.keys(deleteViewDict).includes(currentView.name)}
              on:click={(event) => chooseOptionHandler(event, currentView.name)}
              on:focus={() => currentIndex = index + userViewCollection.length}
              tabindex="0"
              id=view-item-${index}
            >
              <div class="li-upper-content">
                <div class="li-left-content">
                  <div class="view-name">{capitalizeFirstLetter($t(currentView.name))}</div>
                </div>
              </div>
              <div class="li-lower-content">
                <p class="subtitle-view-name">{$t(currentView.user ?? '')}, {$t(currentView.latest_update ?? '')}</p>
              </div>
            </li>
          {/each}
      {/if}
      </ul>

      <!-- Save view -->
      <div class="save-container">
        <div class="input-section">
          <input id="enterViewName" bind:value={newInputName} on:click={(event) => {event.stopPropagation()}} tabindex="0" placeholder={$t("Enter View Name")} />
          <button id="saveButton" class={saveButtonDisabled ? "disabled": ""} disabled={saveButtonDisabled} tabindex="0" on:click={(event) => {saveHandler(event, newInputName)}} title="{$t(buttonText)}">{$t(buttonText)}</button>
        </div>
        {#if savedFlag}
          <p class="save-message success">{message}</p>
        {:else}
          <p class="save-message error">{message}</p>
        {/if}
      </div>

      <div class="old-dashboard-link">
        <p class="dropdown-title">{$t("Dashboard version")}</p>
        <a
          class="old-dashboard-link-switch"
          href="https://visitory.io/{$locale.split('-')[0]}/dashboard/old"
        >{$t("Switch to old dashboard")}</a>
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  @import "../scrollbars";

  .dropdown {
    position: relative;
    cursor: pointer;

    .dropdown-content {
      position: absolute;
      display: flex;
      flex-direction: column;
      z-index: 15;
      max-height: 350px;
      box-sizing: border-box;
      border: 1px solid var(--color-gray);
      border-radius: 0.25rem;
      color: var(--color-black);
      background-color: var(--color-white);
      padding: 0.5rem 1rem;

      // Dropdown opens on the left side of the button
      right: 0%;

      @media screen and (max-width: 768px) {
        width: 95vw;
        right: -10%;
      }
    }

    ul {
      @include scrollbars;
      overflow-y: scroll;
      overflow-x: hidden;
      max-height: 300px;
      min-width: 300px;
      font-size: 1rem;
      padding: 0;
      list-style-type: none;

      li {
        padding: 0.5rem 1rem;
        white-space: nowrap;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        border-bottom: 1px solid black;

        &:hover {
          color: var(--color-white);
          background-color: #35b472cc;
        }

        &.edit-mode {
          color: var(--color-black);
          background-color: var(--color-white);
        }

        &.delete-mode {
          color: var(--color-black);
          background-color: var(--color-white);
        }

        &.selected {
          color: var(--color-tertiary);
        }

        &.focused {
          background-color: #35b472cc;
          outline: none;
        }

        .li-upper-content {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          width: 100%;
        }

        .view-name {
          position: relative;
          cursor: pointer;
          text-align: left;
        }

        .li-lower-content {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          width: 100%;
        }

        .subtitle-view-name {
          font-size: small;
          margin: 0 0 0 0;
        }

        .li-right-content,
        .li-left-content {
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          width: 100%;
          gap: 0.5rem;
        }

        .li-right-content {
          justify-content: flex-end;
        }

        .edit-view-name {
          width: 150px;
          border-bottom: 1px solid black;
          &:hover {
            color: var(--color-black);
          }
        }

        .confirm {
          cursor: pointer;
          background-color: var(--color-white);
          color: var(--color-primary);
          width: 35px;
          height: 35px;

          &:hover {
              color: var(--color-white);
              background-color: var(--color-primary);
              border-radius: 50%;
            }
        }
        .cancel {
          cursor: pointer;
          background-color: var(--color-white);
          color: var(--color-tertiary);
          width: 35px;
          height: 35px;

          &:hover {
            color: var(--color-white);
            background-color: var(--color-tertiary);
            border-radius: 50%;
          }
        }

        .edit-button {
          width: 20px;
          height: 20px;

          &:hover {
            color: var(--color-black);
          }
        }

        .delete-button {
          width: 20px;
          height: 20px;

          &:hover {
            color: var(--color-black);
          }
        }
      }

      .li-empty-list-indicator {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        cursor: default;
        text-align: center;
        color: var(--color-gray-light);

        &:hover {
          color: var(--color-gray-light);
          background-color: var(--color-white);
        }
      }

      .dropdown-title {
        font-size: medium;
        font-weight: bold;
        color: var(--color-black);
        text-align: left;
        padding: 10px 0 5px 7px;
        // background-color: lighten($color: #35b472, $amount: 35%);
        margin: 0;

        &.organization {
          padding-top: 15px;
        }
      }
    }

    .save-container {
      display: flex;
      flex-direction: column;
      border-top: 2px solid var(--color-gray);

      .input-section {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 0.8rem 0.5rem 0.8rem 0.5rem;
        gap: 0.5rem;

          input {
            width: 80%;
            border-bottom: 1px solid black;
            padding: 0.19rem;
            border-radius: 0.1rem;

            &:focus {
              outline: 2px solid var(--color-primary);
            }
        }
      }

      button {
          cursor: pointer;
          background-color: var(--color-white);
          border: 2px solid var(--color-primary);
          border-radius: 0.4rem;
          color: var(--color-black);
          padding: 0 0.5rem;
          height: 35px;

          // When hover change color and background-color with ease
          transition: color 0.2s ease, background-color 0.2s ease;
          &:hover {
            background-color: var(--color-primary);
            color: var(--color-white);
          }

          &.disabled {
            cursor: not-allowed;
            background-color: var(--color-gray);
            color: var(--color-black);
          }
        }

      .save-message {
        margin: 0;
        padding: 0 0.5rem 0.5rem 0.5rem;
        &.success {
          color: var(--color-scrollbar-thumb-hover);
        }

        &.error {
          color: var(--color-tertiary);
        }
      }
    }

    .old-dashboard-link {
      padding: 10px 5px;
      border-top: 2px solid var(--color-gray);

      .dropdown-title {
        font-size: medium;
        font-weight: bold;
        color: var(--color-black);
        text-align: left;
        padding: 0 0 5px 2px;
        margin: 0;
      }

      .old-dashboard-link-switch {
        text-decoration: underline;
        font-size: medium;
        font-weight: normal;
        color: var(--color-primary);
        transition: color 0.2s ease;
        padding: 0 0 5px 0;

        &:visited {
          color: var(--color-primary);
        }

        &:hover,
        &:visited:hover {
          color: #1a7a4a;
        }
      }
    }
  }
</style>
